<template>
  <div class="camera-sidebar bg-white h-full flex flex-col">
    <div class="p-2.5 border-b">
      <!-- Header -->
      <div class="flex items-center justify-between mb-2">
        <h2 class="text-sm font-semibold text-gray-700">
          <i class="fas fa-video mr-2 text-blue-500"></i>
          Cameras
        </h2>
        <button 
          @click="$emit('close')"
          class="lg:hidden p-1 hover:bg-gray-100 rounded-md"
        >
          <i class="fas fa-times text-gray-500"></i>
        </button>
      </div>
      
      <!-- Search -->
      <div class="relative mb-2">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search sites or cameras..."
          class="w-full pl-7 pr-7 py-1.5 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
        />
        <i class="fas fa-search absolute left-2 top-2 text-gray-400 text-xs"></i>
        <button 
          v-if="searchQuery"
          @click="searchQuery = ''"
          class="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
        >
          <i class="fas fa-times text-xs"></i>
        </button>
      </div>
      
      <!-- Site Filter untuk Admin -->
      <div v-if="isAdmin" class="mb-2">
        <label class="block text-[10px] font-medium text-gray-500 mb-1">Filter by Site</label>
        <select
          v-model="siteFilter"
          class="w-full px-2 py-1.5 border rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
          @change="handleSiteFilterChange"
        >
          <option value="all">📡 All Sites</option>
          <option v-for="site in uniqueSites" :key="site" :value="site">
            🏭 {{ site }}
          </option>
        </select>
      </div>
      
      <!-- Active Source Indicator -->
      <div v-if="selectedSourceDisplay !== 'all'" class="mb-2">
        <div class="flex items-center justify-between bg-blue-50 rounded-md px-2 py-1">
          <span class="text-[10px] text-blue-700">
            <i class="fas fa-filter mr-1"></i> Active Source: {{ selectedSourceDisplay }}
          </span>
          <button 
            @click="clearSourceFilter"
            class="text-blue-500 hover:text-blue-700 text-[10px]"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
      
      <!-- Stats Summary -->
      <div class="flex justify-between text-[10px] text-gray-500 bg-gray-50 p-1.5 rounded-md">
        <span><i class="fas fa-video mr-1"></i> {{ filteredCameras.length }} cameras</span>
        <span><i class="fas fa-check-circle text-green-500 mr-1"></i> {{ activeCount }} online</span>
        <span><i class="fas fa-building mr-1"></i> {{ uniqueSites.length }} sites</span>
      </div>
    </div>
    
    <div class="flex-1 overflow-y-auto">
      <div v-if="loading" class="flex flex-col items-center justify-center h-48">
        <div class="spinner-small"></div>
        <p class="text-xs text-gray-500 mt-2">Loading cameras...</p>
      </div>
      
      <!-- Group by Site untuk Admin -->
      <div v-else-if="isAdmin && groupedCameras.length > 0" class="divide-y divide-gray-100">
        <div 
          v-for="group in groupedCameras" 
          :key="group.site"
          class="overflow-hidden"
        >
          <!-- Site Header - Click to select source -->
          <div 
            @click="selectSource(group.site)"
            class="p-2 hover:bg-gray-100 cursor-pointer transition-colors flex items-center justify-between"
            :class="{ 'bg-blue-50': selectedSource === group.site }"
          >
            <div class="flex items-center space-x-2">
              <i class="fas text-xs" :class="expandedSites[group.site] ? 'fa-chevron-down' : 'fa-chevron-right'" @click.stop="toggleSite(group.site)"></i>
              <i class="fas fa-building text-blue-500 text-xs"></i>
              <span class="font-medium text-xs">{{ group.site }}</span>
              <span class="text-[10px] text-gray-500">({{ group.cameras.length }})</span>
            </div>
            <div class="flex items-center space-x-1">
              <span class="w-1.5 h-1.5 rounded-full" :class="getSiteStatus(group.site)"></span>
              <span class="text-[10px] text-gray-500">{{ getSiteOnlineCount(group.site) }}/{{ group.cameras.length }}</span>
              <i v-if="selectedSource === group.site" class="fas fa-check-circle text-blue-500 text-[10px] ml-1"></i>
            </div>
          </div>
          
          <!-- Cameras in Site -->
          <div v-if="expandedSites[group.site]" class="divide-y divide-gray-50">
            <div
              v-for="camera in group.cameras"
              :key="camera.id || camera.name"
              @click="selectCamera(camera)"
              class="p-2 pl-8 hover:bg-gray-50 cursor-pointer transition-colors"
              :class="{ 'bg-blue-50': isSelected(camera) }"
            >
              <div class="flex items-start space-x-2">
                <div 
                  class="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
                  :class="getCameraStatus(camera) ? 'bg-green-100' : 'bg-gray-100'"
                >
                  <i class="fas fa-video text-xs" :class="getCameraStatus(camera) ? 'text-green-600' : 'text-gray-400'"></i>
                </div>
                
                <div class="flex-1 min-w-0">
                  <div class="flex items-center justify-between">
                    <h3 class="font-medium text-gray-800 truncate text-xs">
                      {{ camera.name || 'Unknown' }}
                    </h3>
                    <span 
                      class="w-1.5 h-1.5 rounded-full ml-1"
                      :class="getCameraStatus(camera) ? 'bg-green-500 animate-pulse' : 'bg-gray-300'"
                    ></span>
                  </div>
                  
                  <div class="text-[10px] text-gray-500 mt-0.5">
                    <span class="font-mono">{{ camera.format?.toUpperCase() || 'Unknown' }}</span>
                    <span v-if="camera.location" class="ml-1">• {{ camera.location }}</span>
                  </div>
                  
                  <div class="mt-1 flex items-center justify-between text-[10px]">
                    <span class="text-gray-500">
                      <i class="fas fa-microchip mr-0.5"></i>
                      {{ camera.resolution || 'HD' }}
                    </span>
                    <span class="text-gray-400">
                      {{ getCameraStatus(camera) ? 'Live' : 'Standby' }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- List View untuk Non-Admin / User Biasa -->
      <div v-else-if="!isAdmin && filteredCameras.length > 0" class="divide-y divide-gray-100">
        <div
          v-for="camera in filteredCameras"
          :key="camera.id || camera.name"
          @click="selectCamera(camera)"
          class="p-2 hover:bg-gray-50 cursor-pointer transition-colors"
          :class="{ 'bg-blue-50': isSelected(camera) }"
        >
          <div class="flex items-start space-x-2">
            <div 
              class="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0"
              :class="getCameraStatus(camera) ? 'bg-green-100' : 'bg-gray-100'"
            >
              <i class="fas fa-video text-sm" :class="getCameraStatus(camera) ? 'text-green-600' : 'text-gray-400'"></i>
            </div>
            
            <div class="flex-1 min-w-0">
              <div class="flex items-center justify-between">
                <h3 class="font-medium text-gray-800 truncate text-sm">
                  {{ camera.name || 'Unknown' }}
                </h3>
                <span 
                  class="w-1.5 h-1.5 rounded-full ml-1"
                  :class="getCameraStatus(camera) ? 'bg-green-500 animate-pulse' : 'bg-gray-300'"
                ></span>
              </div>
              
              <div class="text-[10px] text-gray-500 mt-0.5">
                <span class="font-mono">{{ camera.format?.toUpperCase() || 'Unknown' }}</span>
                <span v-if="camera.source" class="ml-1">• {{ camera.source }}</span>
                <span v-if="camera.location" class="ml-1">• {{ camera.location }}</span>
              </div>
              
              <div class="mt-1 flex items-center gap-2 text-[10px] text-gray-500">
                <span><i class="fas fa-microchip mr-0.5"></i> {{ camera.resolution || 'HD' }}</span>
                <span><i class="fas fa-chart-line mr-0.5"></i> {{ camera.fps || 30 }} fps</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Empty State -->
      <div v-else-if="filteredCameras.length === 0" class="flex flex-col items-center justify-center h-48 text-gray-500">
        <i class="fas fa-video-slash text-3xl mb-2 opacity-30"></i>
        <p class="text-xs">No cameras found</p>
        <p v-if="isAdmin && selectedSource !== 'all'" class="text-[10px] mt-1">
          Try selecting a different source or "All Sites"
        </p>
      </div>
    </div>
    
    <!-- Footer Stats -->
    <div class="border-t p-2 bg-gray-50">
      <div class="flex justify-between text-[10px] text-gray-600">
        <span>🟢 Online: {{ activeCount }}</span>
        <span>📹 Total: {{ filteredCameras.length }}</span>
        <span v-if="isAdmin">🏢 Sites: {{ uniqueSites.length }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useAuthStore } from '../../stores/auth'
import { useUIStore } from '../../stores/ui'

const props = defineProps({
  cameras: { type: Array, required: true },
  loading: { type: Boolean, default: false },
  selectedCamera: { type: Object, default: null },
  selectedSource: { type: String, default: 'all' }
})

const emit = defineEmits(['select-camera', 'close', 'select-source'])

const authStore = useAuthStore()
const uiStore = useUIStore()

const searchQuery = ref('')
const siteFilter = ref('all')
const expandedSites = ref({})

const isAdmin = computed(() => authStore.user?.role === 'admin')
const selectedSourceDisplay = computed(() => {
  if (props.selectedSource === 'all') return 'all'
  return props.selectedSource
})

// Get unique sites from cameras
const uniqueSites = computed(() => {
  const sites = new Set()
  props.cameras.forEach(camera => {
    if (camera.source) sites.add(camera.source)
  })
  return Array.from(sites).sort()
})

// Filtered cameras based on search
const filteredCameras = computed(() => {
  let result = [...props.cameras]
  
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter(camera =>
      camera.name?.toLowerCase().includes(query) ||
      camera.source?.toLowerCase().includes(query) ||
      camera.location?.toLowerCase().includes(query)
    )
  }
  
  return result
})

// Group cameras by site for admin view
const groupedCameras = computed(() => {
  if (!isAdmin.value) return []
  
  const groups = new Map()
  
  let camerasToGroup = filteredCameras.value
  
  if (siteFilter.value !== 'all') {
    camerasToGroup = camerasToGroup.filter(c => c.source === siteFilter.value)
  }
  
  camerasToGroup.forEach(camera => {
    const site = camera.source || 'Unknown'
    if (!groups.has(site)) {
      groups.set(site, [])
    }
    groups.get(site).push(camera)
  })
  
  return Array.from(groups.entries())
    .map(([site, cameras]) => ({ site, cameras }))
    .sort((a, b) => a.site.localeCompare(b.site))
})

const activeCount = computed(() => {
  return filteredCameras.value.filter(c => getCameraStatus(c)).length
})

function getCameraStatus(camera) {
  return !!camera.url
}

function getSiteStatus(site) {
  const cameras = props.cameras.filter(c => c.source === site)
  const online = cameras.filter(c => getCameraStatus(c)).length
  if (online === 0) return 'bg-red-500'
  if (online === cameras.length) return 'bg-green-500'
  return 'bg-yellow-500'
}

function getSiteOnlineCount(site) {
  const cameras = props.cameras.filter(c => c.source === site)
  return cameras.filter(c => getCameraStatus(c)).length
}

function toggleSite(site) {
  expandedSites.value[site] = !expandedSites.value[site]
}

function handleSiteFilterChange() {
  expandedSites.value = {}
  if (groupedCameras.value.length > 0) {
    expandedSites.value[groupedCameras.value[0].site] = true
  }
}

function selectCamera(camera) {
  emit('select-camera', camera)
  if (camera.source) {
    uiStore.setSelectedCameraSource(camera.source)
    emit('select-source', camera.source)
  }
}

function selectSource(site) {
  uiStore.setSelectedCameraSource(site)
  emit('select-source', site)
}

function clearSourceFilter() {
  uiStore.setSelectedCameraSource('all')
  emit('select-source', 'all')
}

function isSelected(camera) {
  return props.selectedCamera?.id === camera.id || props.selectedCamera?.name === camera.name
}

watch(groupedCameras, (groups) => {
  if (groups.length > 0 && Object.keys(expandedSites.value).length === 0) {
    expandedSites.value[groups[0].site] = true
  }
}, { immediate: true })

watch(siteFilter, () => {
  expandedSites.value = {}
  if (groupedCameras.value.length > 0) {
    expandedSites.value[groupedCameras.value[0].site] = true
  }
})
</script>

<style scoped>
.camera-sidebar { 
  width: 100%; 
  height: 100%; 
}

.spinner-small {
  width: 24px; 
  height: 24px;
  border: 2px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin { 
  to { transform: rotate(360deg); } 
}

.animate-pulse { 
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite; 
}

@keyframes pulse { 
  0%, 100% { opacity: 1; } 
  50% { opacity: 0.5; } 
}

::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
/* =========================
   🌙 CAMERA SIDEBAR DARK FIX
========================= */

/* Item default */
.dark .camera-sidebar .divide-y > div {
  background-color: transparent;
  color: var(--text-secondary);
}

/* Hover item */
.dark .camera-sidebar .divide-y > div:hover {
  background-color: var(--hover-bg) !important;
  color: var(--text-primary);
}

/* SELECTED CAMERA */
.dark .camera-sidebar .bg-blue-50 {
  background-color: #2a3142 !important;
  color: #e5e7eb !important;
  border-left: 3px solid #60a5fa;
}

/* Hover saat selected */
.dark .camera-sidebar .bg-blue-50:hover {
  background-color: #323a4d !important;
}

/* Text inside selected */
.dark .camera-sidebar .bg-blue-50 .text-gray-800,
.dark .camera-sidebar .bg-blue-50 .text-gray-700,
.dark .camera-sidebar .bg-blue-50 .text-gray-600 {
  color: #e5e7eb !important;
}

/* Sub text */
.dark .camera-sidebar .bg-blue-50 .text-gray-500,
.dark .camera-sidebar .bg-blue-50 .text-gray-400 {
  color: #9ca3af !important;
}

/* Site header (group) */
.dark .camera-sidebar .bg-gray-100 {
  background-color: #1f2937 !important;
}

.dark .camera-sidebar .bg-gray-100:hover {
  background-color: #273244 !important;
}

/* Divider */
.dark .camera-sidebar .divide-gray-100 > * + * {
  border-color: var(--border-color) !important;
}

/* Stats + header */
.dark .camera-sidebar .bg-gray-50 {
  background-color: #111827 !important;
  color: var(--text-secondary);
}

/* Input + select */
.dark .camera-sidebar input,
.dark .camera-sidebar select {
  background-color: #0f172a;
  border-color: var(--border-color);
  color: var(--text-primary);
}
</style>